import type { Tier } from '@busy/api';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
};

// Tier-colored ring around the avatar — matches the demo's avatar-ring treatment for VIP/at-risk.
// Other tiers get no ring (default border).
const TIER_RING: Partial<Record<Tier, string>> = {
  vip: 'ring-2 ring-gold-500 ring-offset-2 ring-offset-surface',
  'at-risk': 'ring-2 ring-red-500 ring-offset-2 ring-offset-surface',
};

// Photo-or-initials avatar. Single source of truth — every list view, detail header,
// and timer screen pulls from this. Falls back to initials if photoUrl is missing
// or fails to load.
export function Avatar({
  name,
  photoUrl,
  size = 'md',
  tier,
}: {
  name: string;
  photoUrl?: string | null;
  size?: Size;
  tier?: Tier;
}) {
  const initials = computeInitials(name);
  const ring = tier ? TIER_RING[tier] ?? '' : '';
  const sizing = SIZE_CLASSES[size];

  if (photoUrl) {
    return (
      <div
        className={`${sizing} ${ring} rounded-full overflow-hidden bg-bg-alt grid place-items-center font-display tracking-wide text-navy-900 dark:text-gold-500 shrink-0`}
        aria-label={name}
      >
        <img
          src={photoUrl}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            // Hide broken image so the initials fallback renders below it
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        <span className="absolute opacity-0 pointer-events-none">{initials}</span>
      </div>
    );
  }

  return (
    <div
      className={`${sizing} ${ring} rounded-full bg-gradient-to-br from-navy-700 to-navy-900 dark:from-gold-400 dark:to-gold-500 grid place-items-center font-display tracking-wide text-white dark:text-navy-900 shrink-0`}
      aria-label={name}
    >
      {initials}
    </div>
  );
}

function computeInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
